import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, CheckCircle2, RefreshCw, Clock, Moon, Sun } from 'lucide-react';

export default function VerifyEmail() {
  const { verifyEmail, resendVerification } = useAuth();
  const { t, isRTL, toggleLanguage, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  const email = location.state?.email || '';
  // WO-21 — the 60-second wait only makes sense right after signup, when a code
  // has just been sent. Someone who lands here from the login screen because
  // their code lapsed needs the Resend button NOW, not in a minute.
  const codeJustSent = location.state?.codeJustSent === true;
  
  const [loading, setLoading] = useState(false);
  const CODE_LEN = 8;
  const [code, setCode] = useState(Array(8).fill(''));
  const [resendTimer, setResendTimer] = useState(codeJustSent ? 60 : 0);
  const [canResend, setCanResend] = useState(!codeJustSent);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);
  const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendTimer > 0 && !canResend) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
  }, [resendTimer, canResend]);

  const toggleDarkMode = () => {
    document.documentElement.classList.toggle('dark');
    setDarkMode(!darkMode);
  };

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    
    // Auto-focus next input
    if (value && index < CODE_LEN - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits entered
    if (value && index === CODE_LEN - 1 && newCode.every(d => d !== '')) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, CODE_LEN);
    if (/^\d+$/.test(pastedData)) {
      const newCode = pastedData.split('').concat(Array(CODE_LEN - pastedData.length).fill(''));
      setCode(newCode);
      if (newCode.every(d => d !== '')) {
        handleSubmit(newCode.join(''));
      }
    }
  };

  const handleSubmit = async (codeString) => {
    const fullCode = codeString || code.join('');
    if (fullCode.length !== CODE_LEN) {
      toast.error(`Please enter all ${CODE_LEN} digits`);
      return;
    }
    
    setLoading(true);
    try {
      await verifyEmail(email, fullCode);
      setVerified(true);
      toast.success(t.verify.success);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      toast.error(err.message);
      setCode(Array(CODE_LEN).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // WO-21 — this used to show "Verification code resent to ..." and send
  // NOTHING: no API existed. A customer whose code had lapsed was told, in
  // hardcoded English, that help was on the way, and then waited for an email
  // that was never sent. It now calls the server and only reports success when
  // the server accepted the request; the boxes are cleared because any digits
  // already typed belong to the code that is being replaced.
  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      await resendVerification(email);
      setCanResend(false);
      setResendTimer(60);
      setCode(Array(CODE_LEN).fill(''));
      inputRefs.current[0]?.focus();
      toast.success(`${t.verify.resendSent} ${email}`);
    } catch (err) {
      toast.error(err.message || t.verify.resendFailed);
    } finally {
      setResending(false);
    }
  };

  if (verified) {
    return (
      <div className={`min-h-screen bg-background flex items-center justify-center p-4 ${isRTL ? 'font-arabic' : ''}`}>
        <div className="fixed inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5 pointer-events-none"></div>
        <Card className="border-2 shadow-xl max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent/20 flex items-center justify-center animate-bounce">
              <CheckCircle2 className="w-10 h-10 text-accent" />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{fontFamily: "'Sora', sans-serif"}}>
              {t.verify.success}
            </h2>
            <p className="text-muted-foreground">
              {t.verify.redirecting}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background flex items-center justify-center p-4 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5 pointer-events-none"></div>
      
      {/* Language & Theme Toggle */}
      <div className={`fixed top-4 ${isRTL ? 'left-4' : 'right-4'} flex items-center gap-2 z-50`}>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleLanguage}
          className="rounded-full"
          data-testid="language-toggle"
        >
          {language === 'en' ? 'عربي' : 'EN'}
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleDarkMode}
          className="rounded-full"
          data-testid="theme-toggle"
        >
          {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/login">
            <img src="/logo.png" alt="Mid2Bid" className="h-12 mx-auto" />
          </Link>
        </div>

        <Card className="border-2 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold" style={{fontFamily: "'Sora', sans-serif"}}>
              {t.verify.title}
            </CardTitle>
            <CardDescription>
              {t.verify.subtitle}
              <br />
              <span className="font-semibold text-foreground">{email}</span>
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-6">
              {/* 8-digit code input */}
              <div className="flex justify-center gap-1.5" dir="ltr">
                {code.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="w-10 h-14 text-center text-xl font-bold"
                    data-testid={`code-input-${index}`}
                  />
                ))}
              </div>

              {/* Timer */}
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>{t.verify.expiresIn} 24 {t.verify.hours}</span>
              </div>

              <Button 
                onClick={() => handleSubmit()}
                className="w-full h-12 text-lg font-semibold"
                disabled={loading || code.some(d => d === '')}
                data-testid="verify-button"
              >
                {loading ? t.verify.verifying : t.verify.verify}
                {!loading && <CheckCircle2 className="ml-2 w-5 h-5" />}
              </Button>

              {/* Resend */}
              <div className="text-center">
                {canResend ? (
                  <Button 
                    variant="ghost" 
                    onClick={handleResend}
                    disabled={resending}
                    className="text-primary"
                    data-testid="resend-button"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${resending ? 'animate-spin' : ''}`} />
                    {resending ? t.verify.resending : t.verify.resend}
                  </Button>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t.verify.resendIn} {resendTimer} {t.verify.seconds}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
